/**
 * PetAlertBadges
 *
 * Global reusable badges for pet alerts shown across the system:
 *   - Aggressive temperament (red)
 *   - Active medication (orange)
 *
 * These appear on any appointment card, boarding dashboard, etc.
 * The data is derived from pet_conditions (temperament) and
 * pet_applications (active medications).
 */

import { useTranslation } from "react-i18next";
import { AlertTriangle, Pill } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Condition names that indicate aggressive behaviour (case-insensitive check)
const AGGRESSIVE_KEYWORDS = ["agressiv", "aggressive", "agress"];

/** A single condition entry as returned by the Supabase select. */
export interface PetConditionEntry {
  condition_id?: string | null;
  conditions?: {
    id?: string | null;
    name?: string | null;
    type?: string | null;
  } | null;
}

/** Determines whether a pet has an aggressive temperament condition. */
export function isPetAggressive(conditions: PetConditionEntry[] | null | undefined): boolean {
  if (!conditions || conditions.length === 0) return false;
  return conditions.some((entry) => {
    const condition = entry?.conditions;
    if (!condition) return false;
    if (condition.type !== "temperament") return false;
    const name = (condition.name ?? "").toLowerCase();
    return AGGRESSIVE_KEYWORDS.some((kw) => name.includes(kw));
  });
}

interface PetAlertBadgesProps {
  /** pet_conditions array from the Supabase select. */
  conditions?: PetConditionEntry[] | null;
  /** Whether to show the medication badge (caller decides based on active applications). */
  hasMedication?: boolean;
  /** If true, shows a stronger red variant for medication (e.g. today's dose not yet given). */
  medicationUrgent?: boolean;
  className?: string;
}

/**
 * Renders alert badges for a pet.
 * Must be wrapped in a TooltipProvider (or use the exported individual badges).
 */
export function PetAlertBadges({
  conditions,
  hasMedication = false,
  medicationUrgent = false,
  className,
}: PetAlertBadgesProps) {
  const { t } = useTranslation("common");
  const aggressive = isPetAggressive(conditions);

  if (!aggressive && !hasMedication) return null;

  return (
    <TooltipProvider>
      <div className={`flex flex-wrap gap-1 ${className ?? ""}`}>
        {aggressive && <AggressiveBadge t={t} />}
        {hasMedication && <MedicationBadge t={t} urgent={medicationUrgent} />}
      </div>
    </TooltipProvider>
  );
}

interface AggressiveBadgeProps {
  t: (key: string) => string;
}

export function AggressiveBadge({ t }: AggressiveBadgeProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="destructive"
          className="gap-1 px-1.5 py-0.5 text-xs font-semibold cursor-default"
          aria-label={t("petAlerts.aggressiveLabel")}
        >
          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
          {t("petAlerts.aggressive")}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>{t("petAlerts.aggressiveTooltip")}</p>
      </TooltipContent>
    </Tooltip>
  );
}

interface MedicationBadgeProps {
  t: (key: string) => string;
  urgent?: boolean;
}

export function MedicationBadge({ t, urgent = false }: MedicationBadgeProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          className={`gap-1 px-1.5 py-0.5 text-xs font-semibold cursor-default ${
            urgent
              ? "bg-red-500 text-white hover:bg-red-600"
              : "bg-orange-500 text-white hover:bg-orange-600"
          }`}
          aria-label={t("petAlerts.medicationLabel")}
        >
          <Pill className="h-3 w-3" aria-hidden="true" />
          {t("petAlerts.medication")}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>
          {urgent ? t("petAlerts.medicationUrgentTooltip") : t("petAlerts.medicationTooltip")}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
